import { useCallback, useEffect, useMemo, useState } from "react";
import StudioSponsorshipSection from "../../../components/midpoint/clientViews/studio/StudioSponsorshipSection";
import {
  createAutomapPartnerSponsorshipApi,
  fetchAutomapSponsorshipSettings,
  patchAutomapSponsorshipSettings,
} from "../../../api/sponsorshipSectionApi";
import "../../../styles/clientViewStudio.css";

export default function AutoMapaSponsorshipPlacementsPage() {
  const sponsorshipApi = useMemo(() => createAutomapPartnerSponsorshipApi(), []);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [workspaceKey, setWorkspaceKey] = useState(0);

  const loadSettings = useCallback(async () => {
    setError("");
    try {
      const body = await fetchAutomapSponsorshipSettings();
      setSettings(body);
    } catch (err) {
      setSettings(null);
      if (err.status === 403) {
        setError(
          "Your partner account does not have AutoMapa access. Sign in with an AutoMapa operator account at the admin login page."
        );
      } else if (err.status === 404) {
        setError(
          "Sponsorship API is unavailable. Restart the API server so the latest AutoMapa partner routes are loaded."
        );
      } else {
        setError(err.message || "Unable to load sponsorship settings.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleToggleCapability = async () => {
    if (!settings) return;
    const nextEnabled = !settings.sponsorshipEnabled;
    setSaving(true);
    setError("");
    try {
      const body = await patchAutomapSponsorshipSettings(nextEnabled);
      await loadSettings();
      if (nextEnabled) {
        setWorkspaceKey((current) => current + 1);
      }
      if (body.seededSlotCount > 0) {
        setWorkspaceKey((current) => current + 1);
      }
    } catch (err) {
      setError(err.message || "Unable to update sponsorship capability.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="muted panel-card">Loading sponsorship placements…</p>;
  }

  return (
    <div className="panel-card automap-sponsorship-admin">
      <header className="automap-sponsorship-admin__header">
        <div>
          <h2>Sponsorship placements</h2>
          <p className="muted">
            Enable sponsored inventory on AutoMapa, define slots, and schedule placements for partner
            accounts.
          </p>
        </div>
        <div className="automap-sponsorship-admin__capability">
          <div>
            <strong>Sponsorship capability</strong>
            <p className="muted">
              {settings?.sponsorshipEnabled
                ? "Live on the published AutoMapa client view."
                : "Disabled — sponsored slots will not render on the public site."}
            </p>
          </div>
          <button
            type="button"
            className={`automap-sponsorship-admin__toggle${settings?.sponsorshipEnabled ? " is-on" : ""}`}
            disabled={saving || !settings}
            onClick={handleToggleCapability}
          >
            {saving ? "Saving…" : settings?.sponsorshipEnabled ? "Enabled" : "Enable sponsorship"}
          </button>
        </div>
      </header>

      {error ? <p className="automap-sponsorship-admin__error">{error}</p> : null}

      {!settings?.sponsorshipEnabled ? (
        <div className="automap-sponsorship-admin__empty panel-card">
          <h3>Turn on sponsorship to get started</h3>
          <p className="muted">
            Enabling sponsorship publishes the feature flag on AutoMapa and seeds the standard
            inventory slots used by home, search, and listing detail templates.
          </p>
          <ul className="cv-studio-list">
            {(settings?.defaultSlots || []).map((slot) => (
              <li key={slot.slotKey}>
                <code>{slot.slotKey}</code> — {slot.name}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <StudioSponsorshipSection
          key={workspaceKey}
          clientViewId={settings.clientViewId}
          canManage
          api={sponsorshipApi}
        />
      )}
    </div>
  );
}
